<template>
  <div v-if="isSuccessScreen || isSubmitted" class="contact-form-success-screen">
    <img src="~widget/assets/images/success-checkmark.svg" alt="Checkmark" />
    <p>{{ $t('CONTACT_FORM.SUCCESS_TEXT_LINE_1') }}</p>
    <p>{{ $t('CONTACT_FORM.SUCCESS_TEXT_LINE_2') }}</p>
  </div>
  <form class="contact-form p-4 " @submit.prevent="onSubmit" v-else>
    <div class="overflow-y-auto inputs-container">
      <div
        class="text-black-800 text-sm leading-5 contact-form-message"
        v-if="isContactFormRenderAsMessage"
      >
        {{ $t('CONTACT_FORM.CHAT_BOT_FORM_TITLE') }}
      </div>
      <div class="text-black-800 text-sm leading-5 contact-form-message" v-else>
        {{
          outOfOfficeMessage
            ? outOfOfficeMessage
            : $t('TEAM_AVAILABILITY.OFFLINE')
        }}
      </div>
      <form-input
        v-model="fullName"
        @onBlur="$v.fullName.$touch"
        maxlength="50"
        class="mt-5"
        :label="$t('CONTACT_FORM.FIELDS.FULL_NAME.LABEL')"
        :placeholder="$t('CONTACT_FORM.FIELDS.FULL_NAME.PLACEHOLDER')"
        type="text"
        :error="
          $v.fullName.$error ? $t('CONTACT_FORM.FIELDS.FULL_NAME.ERROR') : ''
        "
      />
      <form-input
        @onBlur="$v.emailAddress.$touch"
        v-model="emailAddress"
        maxlength="50"
        class="mt-5"
        :label="$t('CONTACT_FORM.FIELDS.EMAIL_ADDRESS.LABEL')"
        :placeholder="$t('CONTACT_FORM.FIELDS.EMAIL_ADDRESS.PLACEHOLDER')"
        type="text"
        :error="
          $v.emailAddress.$error
            ? $t('CONTACT_FORM.FIELDS.EMAIL_ADDRESS.ERROR')
            : ''
        "
      />

      <form-text-area
        v-model="message"
        @onBlur="$v.message.$touch"
        class="my-5"
        :label="$t('CONTACT_FORM.FIELDS.MESSAGE.LABEL')"
        :placeholder="$t('CONTACT_FORM.FIELDS.MESSAGE.PLACEHOLDER')"
        :error="
          $v.message.$error
            ? message.length !== 0
              ? $t('CONTACT_FORM.FIELDS.MESSAGE.ERROR')
              : $t('CONTACT_FORM.FIELDS.MESSAGE.PLACEHOLDER')
            : ''
        "
      />
      <hiver-branding />
    </div>
    <custom-button
      v-if="!isContactFormRenderAsMessage"
      class="font-medium contact-form-submit send-message-mail-button"
      block
      :bg-color="widgetColor"
      :text-color="textColor"
      :disabled="isSending"
    >
      <spinner v-if="isSending" class="p-0" />
      {{ $t('SEND_EMAIL') }}
      <img :src="sendMessageArrowIcon" alt="Send Icon"/>
    </custom-button>
  </form>
</template>

<script>
import CustomButton from 'shared/components/Button';
import FormInput from '../Form/Input';
import FormTextArea from '../Form/TextArea';
import Spinner from 'shared/components/Spinner';
import HiverBranding from 'widget/components/HiverBranding.vue';
import { mapGetters } from 'vuex';
import { getContrastingTextColor } from '@chatwoot/utils';
import {
  required,
  minLength,
  maxLength,
  email
} from 'vuelidate/lib/validators';
import { emailFormatter } from 'widget/helpers/utils.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CHATBOTS_GAINSIGHT_EVENTS, sendGainSightEvent } from '../../../gainSightIntegration';
import sendMessageArrowIcon from '../../assets/images/sendMessageArrowIcon.svg';

export default {
  components: {
    FormInput,
    FormTextArea,
    CustomButton,
    Spinner,
    HiverBranding
  },
   props: {
    type: {
      type: String,
      default: ''
    },
    messageId: {
      type: Number
    },
    isSubmitted: {
      type: Boolean
    }
  },
  validations() {
    const fullNameValidation = {
      fullName: {
        required,
        maxLength: maxLength(50)
      }
    };

    const emailAddressValidation = {
      emailAddress: {
        required,
        email: val => email(emailFormatter(val)),
        maxLength: maxLength(50)
      }
    };

    const messageValidation = {
      message: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(1000)
      }
    };

    let identityValidations = {
      ...identityValidations,
      ...emailAddressValidation,
      ...fullNameValidation
    };

    return {
      ...identityValidations,
      ...messageValidation
    };
  },
  data() {
    return {
      fullName: '',
      emailAddress: '',
      message: '',
      isSending: false,
      isSuccessScreen: false,
      sendMessageArrowIcon
    };
  },
  computed: {
    ...mapGetters({
      widgetColor: 'appConfig/getWidgetColor',
      outOfOfficeMessage: 'appConfig/getOutOfOfficeMessage',
      webChannelConfig: 'appConfig/getWebChannelConfig',
      getWidgetLocationUrl: 'appConfig/getWidgetLocationUrl'
    }),
    textColor() {
      return getContrastingTextColor(this.widgetColor);
    },
    isContactFormRenderAsMessage() {
        return this.type === 'message';
    }
  },
  methods: {
    onSubmit(msgId = 0) {
      if (this.isContactFormRenderAsMessage && msgId != this.messageId) {
        window.bus.$emit('CHAT_CONTACT_FORM_SUBMIT_RESET_STATE');
        return;
      }

      this.$v.$touch();

      if (this.$v.$invalid || this.isSending) {
         if (this.isContactFormRenderAsMessage) {
            window.bus.$emit('CHAT_CONTACT_FORM_SUBMIT_RESET_STATE');
          }
        return;
      }

      this.isSending = true;
      const { websiteToken } = this.webChannelConfig;

      if (this.isContactFormRenderAsMessage) {
        this.$emit('chatFormMessageSubmit', {});
      }
      this.$store
        .dispatch('contacts/sendContactForm', {
          token: websiteToken,
          name: this.fullName,
          email: this.emailAddress,
          message: this.message,
          widgetLocationUrl: this.getWidgetLocationUrl
        })
        .then(() => {
          this.isSending = false;
          this.isSuccessScreen = true;
          if (this.isContactFormRenderAsMessage) {
            window.bus.$emit('CHAT_CONTACT_FORM_SUBMIT_SUCCESS');
            sendGainSightEvent(CHATBOTS_GAINSIGHT_EVENTS.CHATBOT_CONTACT_FORM_SUBMIT);
          }
          setTimeout(() => {
            this.$emit('cfSuccessScreenExpired');
          }, 5000);
        })
    }
  },
  mounted() {
    if (this.isContactFormRenderAsMessage) {
       window.bus.$on(BUS_EVENTS.CHAT_CONTACT_FORM_SUBMIT, (msgId) => this.onSubmit(msgId));
    }
  }
};
</script>
